// src/pages/EditDevice.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { generateClient } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import * as queries from "../graphql/queries";
import * as mutations from "../graphql/mutations";

import PropertyModal from "../components/modals/PropertyModal";
import BuildingModal from "../components/modals/BuildingModal";
import UnitModal from "../components/modals/UnitModal";

import { normalizeGraphQLError } from "../utils/graphqlErrors";
import { getFieldError } from "../utils/formErrors";
import { getAssetTypeOptions } from "../utils/schema/enumUtils.js";
import { useAdminTenant } from "../admin/AdminTenantContext";
import AdminTenantRequired from "../components/admin/AdminTenantRequired";
import {
  fetchProperties,
  fetchBuildingsForProperty,
  fetchUnitsForBuilding,
} from "../lib/LocationLoaders";
import { isDeviceFormDirty } from "../utils/isDeviceFormDirty";

import {
  Autocomplete,
  useAuthenticator,
  Alert,
  Button,
  Card,
  Flex,
  Heading,
  View,
  SelectField,
  Text,
  TextField,
  TextAreaField,
  Divider,
  Loader,
} from "@aws-amplify/ui-react";

// ---------- Amplify client + helper ----------
const client = generateClient();

async function gql(query, variables) {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  const res = await client.graphql({ query, variables, authToken: idToken });
  if (res.errors?.length) throw new Error(res.errors[0].message);
  return res.data;
}

// ---------- Constants ----------
const assetTypeOptions = getAssetTypeOptions();
const LABEL_MAX = 80;
const ROOM_MAX = 80;
const NOTES_MAX = 500;

const normalizeUnitCode = (s = "") =>
  s.toString().toLowerCase().replace(/[-\s]/g, "");

const resolveUnitFromQuery = (query, units) => {
  if (!query) return null;
  const normalizedQuery = normalizeUnitCode(query);
  return units.find((u) => normalizeUnitCode(u.code) === normalizedQuery) || null;
};

export default function EditDevice() {
  const navigate = useNavigate();
  const { deviceId } = useParams(); // route: /devices/:deviceId/edit

  const { tenantKey, isPlatformAdmin } = useAdminTenant();

  // reference lists
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [allUnits, setAllUnits] = useState([]);
  const [roomSuggestions, setRoomSuggestions] = useState([]);

  // device + form
  const [device, setDevice] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const [unitQuery, setUnitQuery] = useState("");

  const [form, setForm] = useState({
    propertyId: "",
    buildingId: "",
    unitId: "",
    room: "",
    assetType: "",
    label: "",
    notes: "",
  });

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [loadingError, setLoadingError] = useState("");

  // Modals
  const [openPropModal, setOpenPropModal] = useState(false);
  const [openBldgModal, setOpenBldgModal] = useState(false);
  const [openUnitModal, setOpenUnitModal] = useState(false);

  const topRef = useRef(null);

  const setGlobalError = (message) =>
    setErrors((prev) => ({ ...prev, global: message || null }));

  function markTouched(name) {
    setTouched((t) => ({ ...t, [name]: true }));
  }

  const onInput = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSelectProperty = (e) => {
    const propertyId = e.target.value;
    setForm((f) => ({ ...f, propertyId, buildingId: "", unitId: "" }));
    setUnitQuery("");
  };

  const onSelectBuilding = (e) => {
    const buildingId = e.target.value;
    setForm((f) => ({ ...f, buildingId, unitId: "" }));
    setUnitQuery("");
  };

  const handleUnitBlur = () => {
    markTouched("unitId");

    if (!unitQuery) {
      setForm((f) => ({ ...f, unitId: "" }));
      return;
    }

    const match = resolveUnitFromQuery(unitQuery, allUnits);
    if (match) {
      setForm((f) => ({ ...f, unitId: match.id }));
    }
  };

  // Scroll to top on global error
  useEffect(() => {
    if (errors?.global) {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [errors?.global]);

  // Load properties
  useEffect(() => {
      (async () => {
        if (!tenantKey) return;
        setLoadingError("");
        try {
          const items = await fetchProperties({ gql, tenantKey });
          setProperties(items);
        } catch (e) {
          console.error(e);
          setLoadingError("Failed to load properties.")
        }
      })();
    }, [tenantKey]);

  // Load room suggestions (optional but nice, same as AddDevice)
  useEffect(() => {
    (async () => {
      if (!tenantKey) return;
      try {
        const result = await gql(queries.devicesByTenantCreatedAt, {
          tenantKey,
          limit: 2000,
        });
        const items = result?.devicesByTenantCreatedAt?.items || [];

        const rooms = items.map((d) => d.room?.trim()).filter(Boolean);
        const freq = {};
        for (const r of rooms) freq[r] = (freq[r] || 0) + 1;

        const unique = Array.from(new Set(rooms));
        unique.sort((a, b) => (freq[b] ?? 0) - (freq[a] ?? 0));
        setRoomSuggestions(unique);
      } catch (e) {
        // non-fatal
        console.warn("Room suggestions failed:", e);
      }
    })();
  }, [tenantKey]);

  // Load device (prefill form)
  useEffect(() => {
    (async () => {
      if (!tenantKey || !deviceId) return;

      setInitialLoading(true);
      setLoadingError("");
      setErrors({});

      try {
        // Adjust this query if your getDevice is named differently
        const data = await gql(queries.getDevice, { id: deviceId });
        const d = data?.getDevice ?? null;

        if (!d) {
          setLoadingError("Device not found.");
          setDevice(null);
          return;
        }

        // Optional safety: ensure tenant match if your schema includes tenantKey on Device
        if (d.tenantKey && d.tenantKey !== tenantKey) {
          setLoadingError("You don’t have access to this device.");
          setDevice(null);
          return;
        }

        setDevice(d);

        // Prefill form
        setForm({
          propertyId: d.propertyId || "",
          buildingId: d.buildingId || "",
          unitId: d.unitId || "",
          room: d.room || "",
          assetType: d.assetType || "",
          label: d.label || "",
          notes: d.notes || "",
        });

        // Prefill unitQuery display string later after units load;
        // for now we’ll keep it empty and fill once we have units.
        setUnitQuery("");
        setTouched({});
      } catch (e) {
        console.error(e);
        setLoadingError("Failed to load device.");
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [tenantKey, deviceId]);

  // Load buildings when property changes
  useEffect(() => {
      (async () => {
        setBuildings([]);
        setAllUnits([]);
        setUnitQuery("");
  
        if (!tenantKey || !form.propertyId) return;
  
        setLoadingError(null);
  
        try {
          const items = await fetchBuildingsForProperty({
            gql,
            tenantKey,
            propertyId: form.propertyId,
          });
          setBuildings(items);
        } catch (e) {
          console.error(e);
          setLoadingError("Failed to load buildings for the selected property.");
        }
      })();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantKey, form.propertyId]);

  // Load units when building changes
    useEffect(() => {
      (async () => {
        setAllUnits([]);
  
        if (!tenantKey || !form.buildingId) return;
  
        try {
          const units = await fetchUnitsForBuilding({
            gql,
            tenantKey,
            buildingId: form.buildingId,
          });
          setAllUnits(units);

          if (form.unitId) {
            const match = units.find((u) => u.id === form.unitId);
            if (match) setUnitQuery(match.code || "");
          }
        } catch (e) {
          console.error(e);
          setLoadingError("Failed to load units for this building.");
        }
      })();
    }, [tenantKey, form.buildingId]);

  // Selected objects
  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === form.propertyId) || null,
    [properties, form.propertyId]
  );

  const selectedBuilding = useMemo(
    () => buildings.find((b) => b.id === form.buildingId) || null,
    [buildings, form.buildingId]
  );


  const isDirty = useMemo(
    () => (device ? isDeviceFormDirty(device, form) : false),
    [device, form]
  );

  const validation = useMemo(() => {
    const v = {};

    v.propertyId = form.propertyId ? null : "Required";
    v.buildingId = form.buildingId ? null : "Required";
    v.assetType = form.assetType ? null : "Required";

    if (form.room && form.room.length > ROOM_MAX) v.room = "Room too long";
    if (form.label && form.label.length > LABEL_MAX) v.label = "Label too long";
    if (form.notes && form.notes.length > NOTES_MAX) v.notes = "Notes too long";

    const unitFromId = form.unitId ? allUnits.find((u) => u.id === form.unitId) : null;

    if (form.unitId && !unitFromId) {
      v.unitId = "Invalid unit selected.";
      return v;
    }

    if (form.unitId && !unitQuery.trim()) {
      v.unitId = "Please select a unit from the list that appears as you type.";
      return v;
    }

    if (unitQuery.trim()) {
      const normalizedQuery = normalizeUnitCode(unitQuery);
      const normalizedFromId = unitFromId ? normalizeUnitCode(unitFromId.code) : null;

      if (!unitFromId || normalizedQuery !== normalizedFromId) {
        v.unitId = "Please select a unit from the list that appears as you type.";
        return v;
      }
    }

    return v;
  }, [form, allUnits, unitQuery]);

  const hasErrors = Object.values(validation).some(Boolean);

  // Field errors (same helper you already use)
  const propertyIdError = getFieldError({ field: "propertyId", touched, validation, errors });
  const buildingIdError = getFieldError({ field: "buildingId", touched, validation, errors });
  const unitIdError = getFieldError({ field: "unitId", touched, validation, errors });
  const assetTypeError = getFieldError({ field: "assetType", touched, validation, errors });
  const roomError = getFieldError({ field: "room", touched, validation, errors });
  const labelError = getFieldError({ field: "label", touched, validation, errors });
  const notesError = getFieldError({ field: "notes", touched, validation, errors });

  // Submit
  const submit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!tenantKey) {
      setGlobalError("Missing tenantKey; ensure the signed-in user has custom:tenantKey2.");
      return;
    }
    if (!deviceId) {
      setGlobalError("Missing deviceId in route.");
      return;
    }
    if (hasErrors) {
      setGlobalError("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const input = {
        id: deviceId,
        propertyId: form.propertyId,
        buildingId: form.buildingId,
        unitId: form.unitId || null,
        room: form.room?.trim() || null,
        assetType: form.assetType || null,
        label: form.label?.trim() || null,
        notes: form.notes?.trim() || null,
      };

      console.log("tenantKey: " + tenantKey);
      const data = await gql(mutations.editDevice, { tenantKey, input });
      const updated = data?.editDevice ?? null;

      if (!updated) throw new Error("No update payload returned.");

      navigate(`/devices/${deviceId}`, {
        replace: true,
        state: { flash: "Device updated successfully." },
      });
    } catch (e2) {
      console.error(e2);
      const mapped = normalizeGraphQLError(e2);
      setErrors(mapped);
    } finally {
      setSubmitting(false);
    }
  };

  // Create modals submitters (same as AddDevice)
  const addProperty = async (payload) => {
    const data = await gql(mutations.addProperty, { tenantKey, input: payload });
    return data?.addProperty ?? { ...payload };
  };

  const handlePropertyCreated = (p) => {
    setProperties((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      map.set(p.id, p);
      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    });
    setForm((f) => ({ ...f, propertyId: p.id, buildingId: "", unitId: "" }));
  };

  const addBuilding = async (payload) => {
    const data = await gql(mutations.addBuilding, { input: payload });
    const b = data?.addBuilding ?? null;
    if (!b) throw new Error("No addBuilding payload returned.");
    return b;
  };

  const handleBuildingCreated = (b) => {
    setBuildings((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      map.set(b.id, b);
      return [...map.values()].sort((a, c) => (a.code || "").localeCompare(c.code || ""));
    });
    setForm((f) => ({ ...f, buildingId: b.id, unitId: "" }));
    setUnitQuery("");
  };

  const addUnit = async (input) => {
    const data = await gql(mutations.addUnit, { tenantKey, input });
    const u = data?.addUnit ?? null;
    if (!u) throw new Error("No addUnit payload returned.");
    return u;
  };

  const handleUnitCreated = (u) => {
    setAllUnits((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      map.set(u.id, u);
      return [...map.values()].sort((a, c) => (a.code || "").localeCompare(c.code || ""));
    });
    setForm((f) => ({ ...f, unitId: u.id }));
    setUnitQuery(u.code || "");
  };

  if (isPlatformAdmin && !tenantKey) {
    return (
      <AdminTenantRequired
        title="Edit Device"
        message="Select a tenant in the top bar before editing anything."
      />
    );
  }

  return (
    <View maxWidth="48rem" margin="0 auto" padding="1.5rem">
      <Flex alignItems="center" justifyContent="space-between" wrap="wrap" gap="0.75rem">
        <View>
          <Heading level={3}>Edit Device</Heading>
          <Text color="font.tertiary" fontSize="0.95rem">
            {deviceId ? `Device: ${deviceId}` : ""}
          </Text>
        </View>

        <Button
          type="button"
          variation="link"
          onClick={() => navigate(deviceId ? `/devices/${deviceId}` : "/devices")}
        >
          Cancel
        </Button>
      </Flex>

      <div ref={topRef} />

      {errors.global && (
        <Alert
          variation="error"
          isDismissible
          onDismiss={() => setErrors((e) => ({ ...e, global: undefined }))}
          heading="Couldn't save changes"
          marginTop="1rem"
        >
          {errors.global}
        </Alert>
      )}

      {loadingError && (
        <Alert variation="error" marginTop="1rem" hasIcon isDismissible={false}>
          {loadingError}
        </Alert>
      )}

      {initialLoading ? (
        <Flex marginTop="2rem" justifyContent="center">
          <Loader />
        </Flex>
      ) : !device ? (
        <Card variation="elevated" marginTop="1.5rem">
          <Heading level={5}>No device loaded</Heading>
          <Text marginTop="0.5rem">Check the URL or your permissions.</Text>
        </Card>
      ) : (
        <View as="form" onSubmit={submit} marginTop="1.5rem">
          <Flex direction="column" gap="1.25rem">
            <Card variation="elevated">
              <Heading level={5} marginBottom="0.75rem">
                Device
              </Heading>

              <Flex direction="column" gap="1.25rem">
                <TextField label="Device ID" value={deviceId || ""} isReadOnly />

                <TextField
                  label="Label (optional)"
                  name="label"
                  value={form.label}
                  onBlur={() => markTouched("label")}
                  hasError={!!labelError}
                  errorMessage={labelError || undefined}
                  onChange={onInput}
                  placeholder="Friendly name"
                />
              </Flex>
            </Card>

            <Card variation="elevated">
              <Heading level={5} marginBottom="0.75rem">
                Location
              </Heading>

              <Flex direction="column" gap="1.25rem">
                <View>
                  <SelectField
                    label="Property"
                    name="propertyId"
                    value={form.propertyId}
                    onChange={onSelectProperty}
                    onBlur={() => markTouched("propertyId")}
                    hasError={!!propertyIdError}
                    errorMessage={propertyIdError || undefined}
                  >
                    <option value="">Select a property…</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </SelectField>

                  <Flex alignItems="center" justifyContent="space-between">
                    <Button type="button" size="small" variation="link" onClick={() => setOpenPropModal(true)}>
                      + New Property
                    </Button>
                  </Flex>
                </View>

                <View>
                  <SelectField
                    label="Building"
                    name="buildingId"
                    value={form.buildingId}
                    onChange={onSelectBuilding}
                    isDisabled={!form.propertyId}
                    onBlur={() => markTouched("buildingId")}
                    hasError={!!buildingIdError}
                    errorMessage={buildingIdError || undefined}
                  >
                    <option value="">
                      {form.propertyId ? "Select a building…" : "Select a property first"}
                    </option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.code}{b.name ? ` — ${b.name}` : ""}
                      </option>
                    ))}
                  </SelectField>

                  <Flex alignItems="center" justifyContent="space-between">
                    <Button
                      type="button"
                      size="small"
                      variation="link"
                      isDisabled={!form.propertyId}
                      onClick={() => setOpenBldgModal(true)}
                    >
                      + New Building (for selected property)
                    </Button>
                  </Flex>
                </View>

                <View>
                  <Autocomplete
                    label="Unit"
                    labelHidden={false}
                    placeholder={form.buildingId ? "Type to search units (e.g., C-12)..." : "Select a building first"}
                    isDisabled={!form.buildingId}
                    onChange={(event) => setUnitQuery(event.target.value)}
                    options={allUnits.map((u) => ({ id: u.id, label: u.code }))}
                    onSelect={(option) => {
                      setForm((f) => ({ ...f, unitId: option?.id || "" }));
                      setUnitQuery(option?.label || "");
                    }}
                    onClear={() => {
                      setForm((f) => ({ ...f, unitId: "" }));
                      setUnitQuery("");
                    }}
                    value={unitQuery}
                    onBlur={handleUnitBlur}
                    hasError={!!unitIdError}
                    errorMessage={unitIdError || undefined}
                  />

                  <Flex alignItems="center" justifyContent="space-between">
                    <Button
                      type="button"
                      size="small"
                      variation="link"
                      isDisabled={!form.buildingId}
                      onClick={() => setOpenUnitModal(true)}
                    >
                      + New Unit (for selected property and building)
                    </Button>
                  </Flex>
                </View>
              </Flex>
            </Card>

            <Card variation="elevated">
              <Heading level={5} marginBottom="0.75rem">
                Asset & Details
              </Heading>

              <Flex direction="column" gap="1.25rem">
                <SelectField
                  label="Asset Type"
                  name="assetType"
                  value={form.assetType}
                  onBlur={() => markTouched("assetType")}
                  hasError={!!assetTypeError}
                  errorMessage={assetTypeError || undefined}
                  onChange={onInput}
                >
                  <option value="">Select type…</option>
                  {assetTypeOptions.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </SelectField>

                <TextField
                  label="Room / Location (optional)"
                  name="room"
                  value={form.room}
                  onBlur={() => markTouched("room")}
                  hasError={!!roomError}
                  errorMessage={roomError || undefined}
                  onChange={onInput}
                  placeholder="e.g., Bathroom, Laundry closet"
                  spellCheck
                  list="room-suggestions"
                />
                <datalist id="room-suggestions">
                  {roomSuggestions.map((r) => (
                    <option key={r} value={r} />
                  ))}
                </datalist>

                <TextAreaField
                  label="Notes (optional)"
                  name="notes"
                  value={form.notes}
                  onBlur={() => markTouched("notes")}
                  hasError={!!notesError}
                  errorMessage={notesError || undefined}
                  onChange={onInput}
                  rows={3}
                  placeholder="Anything helpful for maintenance…"
                />
              </Flex>
            </Card>

            <Divider />

            <Flex alignItems="center" gap="0.75rem">
              <Button
                type="submit"
                isDisabled={!tenantKey || submitting || hasErrors || !isDirty}
                variation={!tenantKey || submitting || hasErrors ? "secondary" : "primary"}
              >
                {submitting ? "Saving…" : "Save Changes"}
              </Button>

              <Button
                type="button"
                variation="link"
                onClick={() => navigate(deviceId ? `/devices/${deviceId}` : "/devices")}
              >
                Cancel
              </Button>
            </Flex>
          </Flex>
        </View>
      )}

      {/* Modals */}
      <PropertyModal
        open={openPropModal}
        onClose={() => setOpenPropModal(false)}
        createPropertyFn={addProperty}
        onCreated={(p) => {
          handlePropertyCreated(p);
          setOpenPropModal(false);
        }}
      />

      <BuildingModal
        open={openBldgModal}
        onClose={() => setOpenBldgModal(false)}
        createBuildingFn={addBuilding}
        buildingProperty={selectedProperty}
        onCreated={(b) => {
          handleBuildingCreated(b);
          setOpenBldgModal(false);
        }}
      />

      <UnitModal
        open={openUnitModal}
        onClose={() => setOpenUnitModal(false)}
        createUnitFn={addUnit}
        unitProperty={selectedProperty}
        unitBuilding={selectedBuilding}
        onCreated={(u) => {
          handleUnitCreated(u);
          setOpenUnitModal(false);
        }}
      />
    </View>
  );
}
